<script setup lang="ts">
import { NuxtLink as regulatorDetailLink } from '#components'
import {
  getLicenseChangeLog,
  getLicenseDetail,
  getLicenseRelatedRisk,
  type LicenseDetail,
  type LicenseDetailSection,
  type LicenseDetailText,
} from '~/server/detail'
import { getRegulatorUrlSlugByCode } from '~/api/regulator-list'
import { useAppStore } from '~/stores/app'
const RIGHT_IMG = 'https://resources1.interface003.com/web30/2026/06/11/f4122ad0-2700-484d-85e5-7fc2ce51220a.png'
const LEFT_IMG = 'https://resources1.interface003.com/web30/2026/06/11/b20f9767-20ee-4d76-b98f-927eb724c8f6.png'
const SHARE_ARROW_ICON = 'https://resources1.interface003.com/web30/2026/07/10/ee8974d3-3ed6-494d-976c-070dee9a6238.png'
const { locale, localePath, t } = useAppI18n()

const normalizeTextValue = (value: unknown) => {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

const getFirstRouteValue = (value: unknown) => {
  if (Array.isArray(value)) return normalizeTextValue(value[0])
  return normalizeTextValue(value)
}
const route = useRoute()
const routeAlias = getFirstRouteValue(route.params.slug)
const { CountryCode } = useAppStore()

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    detail?: (LicenseDetail & Record<string, any>) | null
    loading?: boolean
    projectCode?: string
    exchangeName?: string
    modal?: boolean
    currentIndex?: number
    total?: number
  }>(),
  {
    modal: true,
    currentIndex: 0,
    total: 0,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  previous: []
  next: []
}>()

interface LicenseDialogState {
  detail: LicenseDetail & Record<string, any>
  hiddenTop: boolean
  projectCode: string
  childProjectCode: string
  navigationItems: any[]
  currentIndex: number
}

const matomo = useMatomo()
const trackBottomClick = (type: '证明文件' | '关联风险' | '变更记录') => {
  matomo.trackEvent('licenses_details_bottom', {
    af_type: type,
    af_name: projectCode.value || '',
    af_code: props.exchangeName || '',
  })
}
// 是否是rtl语言
const { isRtl } = useRtl()

const showDialog = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})
const showTipsTitlePopver = ref(false)
const changLogList = ref<any[]>([])
const showRegHis = ref(false)
const showLoading = ref(false)
const isExceeded = ref(false)
const isTypeExceeded = ref(false)
const hasExtraVerticalSpace = ref(false)
const lineClampNum = ref(0)
const showrelatedTrader = ref(false)
const childLicenseLoading = ref(false)
const licenseDialogStack = ref<LicenseDialogState[]>([])
const licenseSwitchAnimating = ref(false)
const hiddenTop = ref(false)
const hasOtherItems = ref(false)
const isMainItemsEven = ref(false)
/** 主信息最后一项内容宽度是否超过当前布局阈值 */
const isLastMainValueWidthExceeded = ref(false)
/** 复用正在进行的子牌照详情请求，避免 hover 和点击重复请求同一份数据。 */
const nestedLicenseRequestMap = new Map<string, Promise<LicenseDetail | null>>()

const tipsTitle = ref<HTMLElement | null>(null)
const relatedTrader = ref<HTMLElement | null>(null)
const attachmentsBtn = ref<HTMLElement | null>(null)
const changeLogCountBtn = ref<HTMLElement | null>(null)
const mainUl = ref<HTMLElement | null>(null)
const otherDiv = ref<HTMLElement | null>(null)
const otherUl = ref<HTMLElement | null>(null)
const textContainer = ref<HTMLElement | null>(null)
const customerTypeText = ref<HTMLElement | null>(null)
const riskstatement = ref<{
  open: (result?: any, id?: string) => void
} | null>(null)
/** 弹窗内容容器 ref，用于约束 ElPopover 不超出红线 */
const popreginfoRef = ref<HTMLElement | null>(null)

const activeLicenseDialog = computed(() => licenseDialogStack.value.at(-1))
const projectCode = computed(() => activeLicenseDialog.value?.projectCode || props.projectCode)
/** 主牌照使用外部分类上下文，子牌照使用当前弹窗栈保存的同组牌照上下文。 */
const showCategoryNavigation = computed(() => licenseDialogStack.value.length === 0 && props.total > 1)
const hasChildNavigationContext = computed(() => (activeLicenseDialog.value?.navigationItems.length || 0) > 0)
const showChildNavigation = computed(() => (activeLicenseDialog.value?.navigationItems.length || 0) > 1)
const showLicenseNavigation = computed(() => showCategoryNavigation.value || showChildNavigation.value)
const showLicenseSequence = computed(() => showCategoryNavigation.value || hasChildNavigationContext.value)
const currentLicenseIndex = computed(() =>
  hasChildNavigationContext.value ? activeLicenseDialog.value?.currentIndex || 0 : props.currentIndex,
)
const currentLicenseTotal = computed(() =>
  hasChildNavigationContext.value ? activeLicenseDialog.value?.navigationItems.length || 0 : props.total,
)
const isFirstLicense = computed(() => currentLicenseIndex.value <= 0)
const isLastLicense = computed(() => currentLicenseIndex.value >= currentLicenseTotal.value - 1)
const licenseSequence = computed(() => `${currentLicenseIndex.value + 1}/${currentLicenseTotal.value}`)
const isLicenseSwitchLoading = computed(() => Boolean(props.loading || childLicenseLoading.value))
const userVip = computed(() => true)
const dealerDefaultLogo =
  'https://resources1.interface003.com/web30/2026/07/08/18c8b8b9-f338-4994-ad79-c6305ff106e1.png'
const isChinese = computed(() => isChineseLocale(locale))
const normalizeText = (value: unknown) => (value === null || value === undefined ? '' : String(value).trim())

const exchangeSlug = computed(() => getFirstRouteValue(route.params.slug))
const buildCertificateHref = (regulationId: unknown, extraQuery: Record<string, unknown> = {}) => {
  const nextRegulationId = normalizeText(regulationId)
  const query = new URLSearchParams()

  if (nextRegulationId) query.set('regulationId', nextRegulationId)
  Object.entries(extraQuery).forEach(([key, value]) => {
    const text = normalizeText(value)
    if (text) query.set(key, text)
  })

  if (exchangeSlug.value) {
    return `/${locale}/exchanges/${exchangeSlug.value}/licenses?${query.toString()}`
  }

  return ''
}
const normalizeStringArray = (value: unknown) => {
  if (Array.isArray(value)) return value.map(normalizeText).filter(Boolean)
  const text = normalizeText(value)
  return text ? [text] : []
}
const textContent = (value?: LicenseDetailText | null) => normalizeText(value?.content)
const useImageFallback = (event: Event, fallback = dealerDefaultLogo) => {
  const img = event.target as HTMLImageElement | null
  if (img && img.src !== fallback) img.src = fallback
}

const firstValue = (sections: LicenseDetailSection[] = [], names: string[]) => {
  const lowerNames = names.map((name) => name.toLowerCase())
  for (const section of sections) {
    const label = textContent(section.type1Content?.label || section.type2Content?.label).toLowerCase()
    if (!lowerNames.some((name) => label.includes(name))) continue
    if (section.type === 1) return section.type1Content?.value
    if (section.type === 2) return section.type2Content?.value?.[0]
  }
  return null
}
const allValues = (sections: LicenseDetailSection[] = [], names: string[]) => {
  const lowerNames = names.map((name) => name.toLowerCase())
  for (const section of sections) {
    const label = textContent(section.type1Content?.label || section.type2Content?.label).toLowerCase()
    if (!lowerNames.some((name) => label.includes(name))) continue
    if (section.type === 1 && section.type1Content?.value) return [section.type1Content.value]
    if (section.type === 2) return section.type2Content?.value || []
  }
  return []
}

const normalizeDetail = (detail?: (LicenseDetail & Record<string, any>) | null): Record<string, any> => {
  const source = (detail || {}) as Record<string, any>
  const sections = (source.sections || []) as LicenseDetailSection[]
  const status = firstValue(sections, ['监管状态', 'regulatory status', 'status'])
  const number = firstValue(sections, ['监管证号', 'license no', 'license number', '证号'])
  const country = firstValue(sections, ['监管国家', 'country', '地区'])
  const effective = firstValue(sections, ['生效时间', 'effective'])
  const holding = firstValue(sections, ['持牌机构', 'licensed', 'holding'])
  const business = allValues(sections, ['业务权限', 'business'])
  const customerType = allValues(sections, ['客户类型', 'customer'])
  const websites = allValues(sections, ['网址', 'website', 'site'])
  const emails = allValues(sections, ['邮箱', 'email'])
  const phones = allValues(sections, ['电话', 'phone'])
  const addresses = allValues(sections, ['地址', 'address'])
  const stamps = sections.flatMap((section: any) => section.type4Content?.stamp || [])
  const header = (source.header || {}) as Record<string, any>
  const bottom = (source.bottom || {}) as Record<string, any>
  const attachments = source.attachments || bottom.files || []
  const bgImage = normalizeStringArray(
    source.bgImage || source.bgImages || source.backgroundImages || source.backgroundImage,
  )
  return {
    ...source,
    bgImage,
    header: {
      ...header,
      logo: header.logo || source.logo || dealerDefaultLogo,
      name: header.name || '',
      licenseName: header.licenseName || '',
      serialNumber: header.serialNumber || source.serialNumber || '',
      regulatorCode: header.regulatorCode || source.regulatorCode || '',
      tip: header.tip || source.tip || {},
      tips: header.tips || source.tips || [],
    },
    regulationStatus: source.regulationStatus || textContent(status),
    regulationStatusBgColor: source.regulationStatusBgColor || status?.bgColor,
    regulationStatusColor: source.regulationStatusColor || status?.color,
    regulationNumber: source.regulationNumber || textContent(number),
    regulationCountryIcon: source.regulationCountryIcon || country?.prefixIcon?.iconUrl || '',
    regulationCountryCode: source.regulationCountryCode || '',
    regulationCountryName: source.regulationCountryName || textContent(country) || '',
    showEffectiveTime: source.showEffectiveTime || textContent(effective) || '',
    holdingCompanyId: source.holdingCompanyId || '',
    holdingCompanyName: source.holdingCompanyName || textContent(holding) || '',
    businessPermissions: source.businessPermissions || business.map(textContent).filter(Boolean),
    customerType: source.customerType || customerType.map(textContent).filter(Boolean),
    websites: source.websites || websites.map(textContent).filter(Boolean),
    emails: source.emails || emails.map(textContent).filter(Boolean),
    phones: source.phones || phones.map(textContent).filter(Boolean),
    addresses: source.addresses || addresses.map(textContent).filter(Boolean),
    attachments,
    identifySeal: source.identifySeal || stamps[0]?.iconUrl || '',
    regulationStatusSeal: source.regulationStatusSeal || stamps[1]?.iconUrl || '',
    updateTime: source.updateTime || bottom.updateTime || '',
    changeLogCount: source.changeLogCount || 0,
    relatedRiskCount: source.relatedRiskCount || 0,
    share: source.share || '',
    shareTraderCodes: source.shareTraderCodes || [],
    replaceItems: source.replaceItems || [],
    bottomLabel: source.bottomLabel || '',
    relatedTrader: source.relatedTrader || {},
    viewMore: source.viewMore || false,
    regulationId: source.regulationId || source.id || '',
  }
}

const item = computed<Record<string, any>>(() => normalizeDetail(activeLicenseDialog.value?.detail || props.detail))
/** 详情接口返回的总变更记录数，异常值统一按 0 处理 */
const changeLogCount = computed(() => {
  const count = Number(item.value?.changeLogCount)
  return Number.isFinite(count) && count > 0 ? count : 0
})
/** 是否存在可查看的变更记录，仅使用详情接口的 hasChangeLog 字段判断 */
const hasChangeLog = computed(() => item.value?.hasChangeLog === true)
const licenseRegulationId = computed(() => normalizeText(item.value?.regulationId))
const licenseProjectCode = computed(() => normalizeText(item.value?.projectCode || projectCode.value))
const certificateHref = computed(() => {
  if (!licenseRegulationId.value || !licenseProjectCode.value) return ''
  return buildCertificateHref(licenseRegulationId.value)
})
/** 当前牌照所属监管机构的 code，用于解析监管列表使用的 urlSlug。 */
const regulatorCode = computed(() => normalizeText(item.value?.header?.regulatorCode || item.value?.regulatorCode))
const { data: regulatorUrlSlugData } = await useAsyncData(
  () => `license-regulator-url-slug-${regulatorCode.value}`,
  () => getRegulatorUrlSlugByCode(regulatorCode.value),
  {
    default: () => '',
    watch: [regulatorCode],
  },
)
/** 牌照所属监管机构详情链接，与监管机构列表使用相同的 urlSlug 路由，并保留原有来源参数。 */
const regulatorDetailHref = computed(() => {
  const regulatorUrlSlug = normalizeText(regulatorUrlSlugData.value)

  if (!regulatorUrlSlug) return ''

  const query: Record<string, string> = {}

  if (exchangeSlug.value) query.exchange = exchangeSlug.value
  if (props.exchangeName) query.exchangeName = props.exchangeName

  return localePath({
    path: `/regulators/${encodeURIComponent(regulatorUrlSlug)}`,
    query,
  })
})
const licenseBackgroundStyle = computed(() => {
  const backgroundImage = normalizeText(item.value?.bgImage?.[0])
  return backgroundImage
    ? {
        backgroundImage: `url('${backgroundImage}')`,
      }
    : {}
})
/** levelIcon 弹窗 popper 配置：preventOverflow 约束在 popreginfo 容器内，防止超出红线 */
const popperOptions = computed(() => ({
  modifiers: [
    {
      name: 'preventOverflow',
      options: {
        boundary: popreginfoRef.value || 'clippingParents',
        padding: {
          top: 0,
          right: 40,
          bottom: 0,
          left: 40,
        },
      },
    },
    {
      name: 'flip',
      options: {
        boundary: popreginfoRef.value || 'clippingParents',
      },
    },
  ],
}))

const bottomHtml = computed(() => {
  const type5Content = (item.value.sections as LicenseDetailSection[] | undefined)?.find(
    (section) => section.type === 5,
  )?.type5Content
  if (type5Content) {
    const label = type5Content?.label?.content || ''

    return (type5Content?.items || []).reduce((html, replaceItem) => {
      const replaceName = replaceItem.replaceName || ''
      if (!replaceName) return html

      const name = replaceItem.name?.content || ''
      const iconUrl = replaceItem.name?.prefixIcon?.iconUrl || ''
      const iconHtml = iconUrl
        ? `<img class="w-[16px] h-[16px] object-cover rounded-[2px]" src="${iconUrl}" alt="${name}"/>`
        : ''
      const replacement = `<div class="flex items-center text-[12px] px-[3px]">${iconHtml}<div class="text-[#164b71]">${name}</div></div>`
      const isolatedReplaceName = `${replaceName}`

      return html.includes(isolatedReplaceName)
        ? html.replace(isolatedReplaceName, replacement)
        : html.replace(replaceName, replacement)
    }, label)
  }
  if (!item.value?.bottomLabel || !item.value?.replaceItems?.[0]) return ''
  const replaceItem = item.value.replaceItems[0]
  const replacement =
    replaceItem.type === 2
      ? `<div class="inline-flex gap-x-[4px] mx-[6px]"><img class="h-[13px] w-auto object-cover relative top-[2px]" src="${replaceItem.eeaFlag || ''}" alt="eea" /><span class="text-[12px] text-[#164B71]">${replaceItem.eeaText || ''}</span></div>`
      : replaceItem.type === 3
        ? `<div class="text-[#164B71] text-[12px]">${replaceItem.items?.[0]?.parentRegulationNumber || ''}</div>`
        : `<div class="flex items-center gap-x-[6px] text-[12px]"><img class="w-[16px] h-[16px] object-cover rounded-[2px]" src="${replaceItem.items?.[0]?.grantorTraderIcon || ''}" alt="grantorTraderIcon"/><div class="text-[#164b71]">${replaceItem.items?.[0]?.grantorTraderName || ''}</div></div>`
  return item.value.bottomLabel.replace(replaceItem?.replaceName, replacement)
})

const getContactFieldsCount = (isTotalCount = false): number | boolean => {
  const basicFields = ['effectiveArea', 'websites', 'emails', 'phones', 'addresses']
  const extraFields = ['industryAbbreviations', 'tradingMethod']
  let fieldCount = 0
  if (item.value?.viewMore) {
    fieldCount += basicFields.some((field) => item.value?.[field]?.length > 0) ? 1 : 0
    extraFields.forEach((field) => {
      if (item.value?.[field]?.length > 0) fieldCount += 1
    })
  } else {
    ;[...basicFields, ...extraFields].forEach((field) => {
      if (item.value?.[field]?.length > 0) fieldCount += 1
    })
  }
  const addNum = (item.value.relatedTrader?.traderName ? 1 : 0) + (item.value.kybContent ? 1 : 0)
  if (isTotalCount) return fieldCount + addNum || addNum
  return (fieldCount + addNum) % 2 === 0
}

//  是否上下排序
const isTopBottom = computed(() => {
  const contactFieldsCount = Number(getContactFieldsCount(true))
  const customerTypeCount = item.value.customerType?.length ? 1 : 0

  return !isChinese.value || contactFieldsCount + customerTypeCount <= 1
})
/** main-ul 的直属 li 为奇数时，返回最后一个可见项字段名，否则返回 false */
const lastMainItemKey = computed(() => {
  if (!mainUl.value || isMainItemsEven.value) return false

  const contactTypes = ['effectiveArea', 'websites', 'emails', 'phones', 'addresses']
  const visibleContactTypes = item.value?.viewMore
    ? contactTypes.filter((type) => item.value?.[type]?.length > 0).slice(0, 1)
    : contactTypes.filter((type) => item.value?.[type]?.length > 0)
  const visibleItemKeys = [
    ...visibleContactTypes,
    ...(item.value?.industryAbbreviations ? ['industryAbbreviations'] : []),
    ...(item.value?.tradingMethod ? ['tradingMethod'] : []),
    ...(item.value?.customerType?.length && !getContactFieldsCount() ? ['customerType'] : []),
  ]

  return visibleItemKeys.at(-1) || ''
})

const lineClampClass = computed(() => ({
  'line-clamp-2': lineClampNum.value === 2,
  'line-clamp-3': lineClampNum.value === 3,
  'line-clamp-4': lineClampNum.value === 4,
  'line-clamp-5': lineClampNum.value === 5,
}))

const HAS_MORE_HEIGHT = 31
const ONE_LI_HEIGHT = 31
const IS_TOP_BOTTOM_LI_HEIGHT = 56
const EVEN_MAIN_OTHER_MIN_HEIGHT = 98
const liHeight = computed(() => (isTopBottom.value ? IS_TOP_BOTTOM_LI_HEIGHT : ONE_LI_HEIGHT))
// 根据主列表布局状态计算其他信息区域的最小高度
const otherLayoutStyle = computed(() => {
  let otherUlMinHeight = EVEN_MAIN_OTHER_MIN_HEIGHT

  // if (item.value?.viewMore) {
  //   otherUlMinHeight += HAS_MORE_HEIGHT
  // }

  if (!isLastMainValueWidthExceeded.value && !isMainItemsEven.value) {
    otherUlMinHeight -= liHeight.value
  }

  return {
    '--has-more-height': `${HAS_MORE_HEIGHT}px`,
    '--other-ul-min-height': `${otherUlMinHeight}px`,
  }
})

/** 弹窗打开时支持键盘左右方向键按视觉方向切换，RTL 下同步反转牌照步进方向。 */
const handleLicenseKeydown = (event: KeyboardEvent) => {
  const target = event.target
  const isEditing =
    target instanceof HTMLElement &&
    (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))

  if (
    event.defaultPrevented ||
    isEditing ||
    !props.modelValue ||
    !showLicenseNavigation.value ||
    isLicenseSwitchLoading.value
  )
    return

  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return

  const direction: -1 | 1 = event.key === 'ArrowLeft' ? (isRtl.value ? 1 : -1) : isRtl.value ? -1 : 1
  const isBoundaryLicense = direction === -1 ? isFirstLicense.value : isLastLicense.value
  if (isBoundaryLicense) return

  event.preventDefault()
  switchVisibleLicense(direction)
}

watch(
  [() => props.modelValue, item],
  ([v]) => {
    if (v) {
      isMainItemsEven.value = false
      isLastMainValueWidthExceeded.value = false
      void updateDialogLayoutState()
      if (import.meta.client) document.body.style.setProperty('overflow', 'hidden', 'important')
    } else if (import.meta.client) {
      licenseDialogStack.value = []
      document.body.style.setProperty('overflow', 'auto', 'important')
      document.body.style.setProperty('padding', '0', 'important')
      isExceeded.value = false
      isTypeExceeded.value = false
      hasExtraVerticalSpace.value = false
      lineClampNum.value = 0
      hasOtherItems.value = false
      isMainItemsEven.value = false
      isLastMainValueWidthExceeded.value = false
      // regulationCountryCountOffset.value = 0
      // kybOffset.value = 0
    }
  },
  { flush: 'post' },
)
onMounted(() => {
  window.addEventListener('keydown', handleLicenseKeydown)
})
onBeforeUnmount(() => {
  if (import.meta.client) {
    document.body.style.setProperty('overflow', 'auto', 'important')
    window.removeEventListener('keydown', handleLicenseKeydown)
  }
})

const getTipsTitleHeight = () => {
  const sw = tipsTitle.value?.scrollWidth || 0
  const cw = tipsTitle.value?.clientWidth || 0
  showTipsTitlePopver.value = sw > cw
}
const isTextTruncated = (el: HTMLElement | null) => {
  if (!el || !el.textContent || !import.meta.client) return false
  const computedStyle = window.getComputedStyle(el)
  const containerWidth = el.clientWidth
  const measureEl = document.createElement('span')
  measureEl.textContent = el.textContent
  measureEl.style.cssText = `position:absolute;top:-9999px;left:-9999px;visibility:hidden;white-space:nowrap;font-size:${computedStyle.fontSize};font-weight:${computedStyle.fontWeight};letter-spacing:${computedStyle.letterSpacing};`
  document.body.appendChild(measureEl)
  const fullTextWidth = measureEl.offsetWidth
  document.body.removeChild(measureEl)
  return fullTextWidth > containerWidth
}
const getRelatedTraderWidth = () => (showrelatedTrader.value = isTextTruncated(relatedTrader.value))
const alignButtonWidths = () => {
  if (attachmentsBtn.value && attachmentsBtn.value?.style) attachmentsBtn.value.style.width = ''
  if (changeLogCountBtn.value && changeLogCountBtn.value?.style) changeLogCountBtn.value.style.width = ''
  if (
    !attachmentsBtn.value ||
    !attachmentsBtn.value?.style ||
    !changeLogCountBtn.value ||
    !changeLogCountBtn.value?.style
  )
    return
  const maxWidth = Math.max(attachmentsBtn.value.offsetWidth, changeLogCountBtn.value.offsetWidth)
  attachmentsBtn.value.style.width = `${maxWidth}px`
  changeLogCountBtn.value.style.width = `${maxWidth}px`
}
// const getOffsetFromKyb = () => {
//   if (!item.value?.kybIcon) return
//   const li = kybLi.value
//   const countEl = kybImg.value
//   if (!li || !countEl) return 0
//   const liRect = li.getBoundingClientRect()
//   const countRect = countEl.getBoundingClientRect()
//   kybOffset.value = countRect.right - liRect.right + 17
// }
// const getOffsetFromLi = () => {
//   if (
//     (item.value?.replaceItems?.[0] && item.value?.replaceItems?.[0].type !== 2) ||
//     item.value?.regulationCountryCount === 0 ||
//     !item.value?.regulationCountryCount
//   )
//     return
//   const li = regulationCountryLi.value
//   const countEl = regulationCountryCount.value
//   if (!li || !countEl) return 0
//   regulationCountryCountOffset.value = -(countEl.getBoundingClientRect().left - li.getBoundingClientRect().left)
// }
const updateTextHeight = () => {
  const div = otherDiv.value
  const ul = otherUl.value
  if (!div || !ul) return
  const diff = div.offsetHeight - ul.offsetHeight
  if (diff < 25) lineClampNum.value = 3
  else if (diff < 50) lineClampNum.value = 4
  else if (diff < 75) lineClampNum.value = 5
  else lineClampNum.value = 2
  isExceeded.value = false
  if (textContainer.value)
    nextTick(() => (isExceeded.value = textContainer.value!.scrollHeight > textContainer.value!.clientHeight))
}
/** 根据主信息直属列表项数量更新双数状态 */
const updateMainItemsState = () => {
  const mainItemsCount = mainUl.value?.children.length || 0
  isMainItemsEven.value = mainItemsCount > 0 && mainItemsCount % 2 === 0
  updateLastMainValueWidthState()
}
/** 根据最后一项值节点的实际内容宽度更新其他模块最小高度 */
const updateLastMainValueWidthState = () => {
  const lastMainItem = mainUl.value?.lastElementChild as HTMLElement | null
  const lastValue = lastMainItem?.lastElementChild as HTMLElement | null
  if (!lastValue) {
    isLastMainValueWidthExceeded.value = false
    return
  }

  const valueNodes = [lastValue, ...lastValue.querySelectorAll<HTMLElement>('*')]
  const actualWidth = Math.max(...valueNodes.map((node) => node.scrollWidth))
  const maxWidth = isTopBottom.value ? 458 : 384
  isLastMainValueWidthExceeded.value = actualWidth > maxWidth
}
/** 在 Vue 更新和浏览器布局完成后重新测量当前牌照内容 */
const updateDialogLayoutState = async () => {
  await nextTick()
  if (!import.meta.client) return

  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
  checkCurrentText()
  updateTextHeight()
  updateMainItemsState()
  updateOtherItemsState()
  alignButtonWidths()
  getRelatedTraderWidth()
  getTipsTitleHeight()
}
/** 更新其他模块是否存在列表项 */
const updateOtherItemsState = () => {
  hasOtherItems.value = Boolean(otherUl.value?.children.length)
}

const openMsbFn = () => {
  const obj = item.value?.sections?.[0].type5Content.items?.[0].tap
  if (!obj) return
  toggleLicence({
    projectCode: projectCode.value,
    targetId: obj?.targetId,
    hiddenTop: true,
    childProjectCode: '',
  })
}
const openFn = () => {
  const obj = item.value?.replaceItems?.[0]
  if (!obj) return
  if (obj.type === 3)
    toggleLicence({
      projectCode: obj?.items?.[0]?.projectCode,
      targetId: obj?.items?.[0]?.parentRegulationId,
      hiddenTop: true,
      childProjectCode: item.value?.relatedTrader?.traderCode,
    })
  else if (obj.type === 1) {
    navigateTo(
      localePath({
        name: 'exchange-detail',
        params: {
          slug: obj?.items?.[0]?.alias,
        },
      }),
      {
        open: {
          target: '_blank',
        },
      },
    )
  }
}
const viewMoreFn = (v: string) => {
  const contactTypes = ['effectiveArea', 'websites', 'emails', 'phones', 'addresses']
  const itemName = contactTypes.find((type) => item.value?.[type]?.length > 0) || ''
  return v === itemName
}
/** 打开 EEA 子牌照，并保存全部同级子牌照供弹窗左右切换。 */
const openChildLicense = (obj: any, items: any[] = [], index = 0) => {
  const navigationItems = items.map((childItem) => childItem?.tap).filter(Boolean)
  const targetId = obj?.targetId || obj?.regulationId
  const currentIndex = navigationItems.findIndex(
    (navigationItem) => (navigationItem?.targetId || navigationItem?.regulationId) === targetId,
  )

  toggleLicence(
    {
      ...obj,
      childProjectCode: item.value?.relatedTrader?.traderCode || '',
    },
    {
      navigationItems,
      currentIndex: currentIndex >= 0 ? currentIndex : index,
    },
  )
}

/** 共用父牌照蒙层；子牌照打开时点击蒙层只关闭子牌照。 */
const handleDialogBeforeClose = (done: () => void) => {
  if (licenseDialogStack.value.length > 0) {
    closeCurrentLicense()
    return
  }
  done()
}

/** 重新触发牌照内容入场动画。 */
const playLicenseSwitchAnimation = () => {
  licenseSwitchAnimating.value = false
  nextTick(() => (licenseSwitchAnimating.value = true))
}

/** 按接口请求参数生成子牌照请求唯一标识。 */
const getNestedLicenseRequestKey = (projectCodeValue: string, regulationId: string, childProjectCode: string) => {
  return `${projectCodeValue}_${regulationId}_${childProjectCode}`
}

/** 请求子牌照详情，并在请求完成后释放进行中状态。 */
const requestNestedLicenseDetail = (projectCodeValue: string, regulationId: string, childProjectCode: string) => {
  const requestKey = getNestedLicenseRequestKey(projectCodeValue, regulationId, childProjectCode)
  const pendingRequest = nestedLicenseRequestMap.get(requestKey)
  if (pendingRequest) return pendingRequest

  const request = getLicenseDetail(projectCodeValue, regulationId, childProjectCode).finally(() => {
    nestedLicenseRequestMap.delete(requestKey)
  })
  nestedLicenseRequestMap.set(requestKey, request)

  return request
}

/** 获取关联牌照详情，优先使用本地缓存。 */
const loadNestedLicenseDetail = async (obj: any) => {
  const regulationId = obj.targetId || obj.regulationId
  if (!regulationId) return null

  const cachedDetail = getLicenseData(regulationId)
  if (cachedDetail?.regulationId) return cachedDetail

  return await requestNestedLicenseDetail(
    obj?.projectCode || projectCode.value || '',
    regulationId,
    obj?.childProjectCode || '',
  )
}

/** 悬停时预取子牌照详情并写入 24 小时本地缓存。 */
const handleHoverChildLicense = async (obj: any) => {
  const regulationId = obj?.targetId || obj?.regulationId
  if (!regulationId || getLicenseData(regulationId)) return

  try {
    const detail = await requestNestedLicenseDetail(
      obj?.projectCode || projectCode.value || '',
      regulationId,
      obj?.childProjectCode || item.value?.relatedTrader?.traderCode || '',
    )
    setLicenseData(regulationId, detail)
  } catch {
    // 预取失败不影响用户后续点击，点击时仍可重新请求。
  }
}

/** 打开关联牌照；子牌照会同时记录同组切换上下文。 */
const toggleLicence = async (
  obj: any,
  navigation: {
    navigationItems?: any[]
    currentIndex?: number
  } = {},
) => {
  if (childLicenseLoading.value) return
  childLicenseLoading.value = true
  try {
    const detail = await loadNestedLicenseDetail(obj)
    if (detail?.regulationId) {
      const nextHiddenTop = obj?.hiddenTop || false
      licenseDialogStack.value.push({
        detail,
        hiddenTop: nextHiddenTop,
        projectCode: obj?.projectCode || projectCode.value || '',
        childProjectCode: obj?.childProjectCode || '',
        navigationItems: navigation.navigationItems || [],
        currentIndex: navigation.currentIndex || 0,
      })
      hiddenTop.value = nextHiddenTop
      playLicenseSwitchAnimation()
    }
  } finally {
    childLicenseLoading.value = false
  }
}

/** 在当前 EEA 子牌照组内切换，详情加载成功后替换栈顶牌照。 */
const switchChildLicense = async (direction: -1 | 1) => {
  const currentDialog = activeLicenseDialog.value
  if (!currentDialog || childLicenseLoading.value) return

  const nextIndex = currentDialog.currentIndex + direction
  const nextItem = currentDialog.navigationItems[nextIndex]
  if (!nextItem) return

  childLicenseLoading.value = true
  try {
    const detail = await loadNestedLicenseDetail({
      ...nextItem,
      projectCode: nextItem?.projectCode || currentDialog.projectCode,
      childProjectCode: currentDialog.childProjectCode,
    })
    if (!detail?.regulationId) return

    licenseDialogStack.value[licenseDialogStack.value.length - 1] = {
      ...currentDialog,
      detail,
      projectCode: nextItem?.projectCode || currentDialog.projectCode,
      currentIndex: nextIndex,
    }
    hiddenTop.value = currentDialog.hiddenTop
    playLicenseSwitchAnimation()
    void updateDialogLayoutState()
  } finally {
    childLicenseLoading.value = false
  }
}

/** 根据当前展示层级切换主牌照或 EEA 子牌照。 */
const switchVisibleLicense = (direction: -1 | 1) => {
  if (showChildNavigation.value) {
    void switchChildLicense(direction)
    return
  }

  emit(direction === -1 ? 'previous' : 'next')
}
const getChangLog = async (regulationId: string) => {
  if (!hasChangeLog.value || !regulationId) return
  trackBottomClick('变更记录')
  if (userVip.value) {
    const res: any = await getLicenseChangeLog(regulationId).catch(() => null)
    const rest = res?.wikifxagg?.regulatorregulationchangelogv2 || res?.data || res
    if (rest?.success || Array.isArray(rest?.items) || Array.isArray(rest)) {
      changLogList.value = rest?.data?.items || rest?.items || rest || []
      showRegHis.value = true
    }
  } else showRegHis.value = true
}
const openrisk = async (licenseItem: any) => {
  trackBottomClick('关联风险')
  const relatedrisk: any = await getLicenseRelatedRisk(licenseItem.regulationId, projectCode.value || '').catch(
    () => null,
  )
  if (relatedrisk?.code === 0) {
    const data = relatedrisk?.data || {}
    riskstatement.value?.open(data, licenseItem.regulationId)
  }
}
/** 关闭当前牌照；存在父级时回退父牌照，否则关闭整个弹窗。 */
const closeCurrentLicense = () => {
  if (licenseDialogStack.value.length > 0) {
    licenseDialogStack.value.pop()
    hiddenTop.value = activeLicenseDialog.value?.hiddenTop || false
    playLicenseSwitchAnimation()
    return
  }
  showDialog.value = false
}
const closeAll = () => (showDialog.value = false)
const closeReghisFn = () => (showRegHis.value = false)
/** 检查客户类型文本换行数，超过 1 行（达到 2 行及以上）时标记为已超出 */
const checkCurrentText = () => {
  nextTick(() => {
    const el = customerTypeText.value
    if (!el) {
      isTypeExceeded.value = false
      return
    }
    // 依据行高计算实际行数；lineHeight 解析失败（如 normal）时回退为可视高度比较
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight)
    isTypeExceeded.value = Number.isNaN(lineHeight)
      ? el.scrollHeight > el.clientHeight
      : el.scrollHeight / lineHeight >= 2
  })
}
const joinField = (value: string[]) => (isChinese.value ? value?.join('，') : value?.toString())

// 打开交易所详情页
const ExchangeDetailUrl = (alias: string) => {
  return localePath({
    name: 'exchange-detail',
    params: {
      slug: alias,
    },
  })
}

const getLicenseData = (regulationId: string) => {
  const key = `${routeAlias}_${locale}_${CountryCode}_licenseData_${regulationId}`
  const raw = window.localStorage.getItem(key)
  if (!raw) return null
  const { data, expire } = JSON.parse(raw)
  if (Date.now() > expire) {
    window.localStorage.removeItem(key)
    return null
  }
  return data
}

/** 将牌照详情写入 24 小时本地缓存，主牌照和子牌照共用同一缓存格式。 */
const setLicenseData = (regulationId: string, licenseDetail: unknown) => {
  if (!regulationId || !licenseDetail) return

  const LICENSE_CACHE_DURATION = 24 * 60 * 60 * 1000
  const key = `${routeAlias}_${locale}_${CountryCode}_licenseData_${regulationId}`
  window.localStorage.setItem(
    key,
    JSON.stringify({
      data: licenseDetail,
      expire: Date.now() + LICENSE_CACHE_DURATION,
    }),
  )
}
// 缓存存储牌照数据
const localStoreLicenseDataFunc = async () => {
  const obj = item.value?.replaceItems?.[0]
  if (!obj?.items?.[0]?.parentRegulationId) return
  if (obj?.type === 3) {
    const parentObj = {
      projectCode: obj?.items?.[0]?.projectCode,
      targetId: obj?.items?.[0]?.parentRegulationId,
      hiddenTop: true,
      childProjectCode: item.value?.relatedTrader?.traderCode,
    }

    const regulationId = parentObj.targetId
    if (!regulationId) return
    let licenseDetail = null
    if (getLicenseData(regulationId)) {
      licenseDetail = getLicenseData(regulationId)
    } else {
      licenseDetail = await getLicenseDetail(parentObj.projectCode || '', regulationId, '')
    }
    const EXPIRE = 24 * 60 * 60 * 1000
    window.localStorage.setItem(
      `${routeAlias}_${locale}_${CountryCode}_licenseData_${regulationId}`,
      JSON.stringify({
        data: licenseDetail,
        expire: Date.now() + EXPIRE,
      }),
    )
  }
}
</script>

<template>
  <ClientOnly>
    <ElDialog
      v-if="item?.regulationId"
      v-model="showDialog"
      width="800px"
      class="dialog-regulatory xy-fixed !m-0 !rounded-[28px] !bg-[#fff] !p-0"
      body-class="!p-[12.48px] !bg-[transparent]"
      header-class="hidden"
      :close-on-click-modal="false"
      :modal="modal"
      :before-close="handleDialogBeforeClose"
      @opened="updateDialogLayoutState"
      @close="closeAll"
    >
      <button
        v-if="showLicenseNavigation"
        v-show="!isFirstLicense"
        type="button"
        class="license-category-arrow absolute top-1/2 -left-[100px] z-[101] flex size-[40px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-[12px] border-0 bg-[rgba(5,12,31,0.46)] text-white transition-colors hover:bg-[rgba(0,0,0,0.68)] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-[rgba(5,12,31,0.46)] rtl:-right-[100px] rtl:left-auto rtl:rotate-180"
        :disabled="isFirstLicense || isLicenseSwitchLoading"
        aria-label="上一张牌照"
        @click.stop="switchVisibleLicense(-1)"
      >
        <CommonImage :src="LEFT_IMG" alt="left-arrow" class="!size-[20px] object-cover" loading="lazy" />
      </button>
      <button
        v-if="showLicenseNavigation"
        v-show="!isLastLicense"
        type="button"
        class="license-category-arrow absolute top-1/2 -right-[100px] z-[101] flex size-[40px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-[12px] border-0 bg-[rgba(5,12,31,0.46)] text-white transition-colors hover:bg-[rgba(0,0,0,0.68)] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-[rgba(5,12,31,0.46)] rtl:right-auto rtl:-left-[100px] rtl:rotate-180"
        :disabled="isLastLicense || isLicenseSwitchLoading"
        aria-label="下一张牌照"
        @click.stop="switchVisibleLicense(1)"
      >
        <CommonImage :src="RIGHT_IMG" alt="right-arrow" class="!size-[20px] object-cover" loading="lazy" />
      </button>

      <div
        v-if="showLicenseSequence"
        class="absolute !-bottom-[42px] left-[50%] z-[101] flex h-[28px] -translate-x-[50%] items-center justify-center rounded-[1000px] bg-[rgba(5,12,31,0.46)] px-[16px] text-white rtl:right-[50%] rtl:left-auto rtl:translate-x-[50%]"
      >
        {{ licenseSequence }}
      </div>

      <div
        v-if="isLicenseSwitchLoading"
        class="absolute inset-0 z-[102] flex items-center justify-center rounded-[28px] bg-[rgba(5,12,31,0.28)]"
        aria-label="牌照加载中"
        role="status"
      >
        <span class="license-switch-spinner" aria-hidden="true"></span>
      </div>

      <div
        :class="{ 'license-switch-animation': licenseSwitchAnimating }"
        @animationend="licenseSwitchAnimating = false"
      >
        <div
          class="absolute top-[12px] right-[12px] z-[100] flex size-[26.7px] shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-[rgba(5,12,31,0.46)] hover:bg-[rgba(0,0,0,0.6)] rtl:right-auto rtl:left-[12px]"
          @click.stop="closeCurrentLicense"
        >
          <CommonImage
            src="https://resources1.interface003.com/web30/2026/07/18/6bd8e53b-14dd-44be-9553-fcc12719c1a0.png"
            alt="close-icon"
            class="!size-[11.3px] object-cover"
            loading="lazy"
          />
        </div>

        <div>
          <div
            id="popreginfo"
            ref="popreginfoRef"
            :style="licenseBackgroundStyle"
            class="dialog-container relative w-full bg-no-repeat px-[40px] pb-[24px]"
            :class="['h-full', item?.header?.tip?.title ? 'pt-[48px]' : 'pt-[23px]']"
          >
            <div
              class="absolute right-[40px] bottom-[43px] z-[1] flex items-center gap-x-[10px] rtl:right-auto rtl:left-[40px]"
            >
              <div
                v-if="item?.identifySeal || item?.regulationStatusSeal"
                class="flex !h-[94px] !min-w-[94px] shrink-0 gap-x-[10px] overflow-hidden rtl:flex-row-reverse"
              >
                <CommonImage
                  v-if="item?.identifySeal"
                  :src="item?.identifySeal"
                  alt="identifySeal"
                  class="size-[94px] object-cover"
                /><CommonImage
                  v-if="item?.regulationStatusSeal"
                  :src="item?.regulationStatusSeal"
                  alt="regulationStatusSeal"
                  class="size-[94px] object-cover"
                />
              </div>
            </div>

            <div
              v-if="
                !showTipsTitlePopver &&
                item?.header?.tip?.title &&
                (item?.header?.tips?.length === 1 || !item?.header?.tips?.length)
              "
              :style="{
                background: item?.header?.tip?.bgColor,
                boxShadow: `0 1px 0 0 ${item?.header?.tip?.bgColor}`,
              }"
              class="absolute top-[18px] right-[18px] left-[18px] z-[1] flex items-center justify-between gap-x-[20px] p-[6px_8px]"
            >
              <div class="flex items-center">
                <div class="mr-[4px] flex !size-[16px] shrink-0 overflow-hidden rtl:mr-[0px] rtl:ml-[4px]">
                  <CommonImage :src="item?.header?.tip?.ico" class="size-full object-cover" alt="ico" />
                </div>
                <div
                  ref="tipsTitle"
                  :style="{
                    color: item?.header?.tip?.color,
                  }"
                  class="top-10 h-[16px] max-w-[583px] truncate text-[12px] leading-[16px]"
                >
                  {{ item?.header?.tip?.title }}
                </div>
              </div>
              <div class="text-[12px] whitespace-nowrap text-[#292A2E]/60">
                {{ item?.header?.serialNumber }}
              </div>
            </div>
            <ElPopover
              v-else-if="
                showTipsTitlePopver &&
                item?.header?.tip?.title &&
                (item?.header?.tips?.length === 1 || !item?.header?.tips?.length)
              "
              :teleported="false"
              popper-class="tips-popover !p-[12px_16px] max-w-[740px]"
              placement="bottom"
              :show-after="200"
              :popper-options="popperOptions"
              ><div class="flex gap-x-[4px]">
                <div class="mt-[2px] flex !size-[16px] shrink-0 overflow-hidden">
                  <CommonImage :src="item?.header?.tip?.ico" class="size-full object-cover" alt="ico" />
                </div>
                <div class="min-h-[18px] text-[12px] leading-[18px]">
                  {{ item?.header?.tip?.title }}
                </div>
              </div>
              <template #reference
                ><div
                  v-if="item?.header?.tip?.title"
                  :style="{
                    width: '740px',
                    background: item?.header?.tip?.bgColor,
                  }"
                  class="absolute top-[18px] right-[18px] left-[18px] z-[1] flex cursor-pointer items-center justify-between gap-x-[20px] p-[6px_8px]"
                >
                  <div class="flex items-center gap-x-[4px]">
                    <div class="mt-[2px] flex !size-[16px] shrink-0 overflow-hidden">
                      <CommonImage :src="item?.header?.tip?.ico" class="size-full object-cover" alt="ico" />
                    </div>
                    <div
                      ref="tipsTitle"
                      :style="{
                        color: item?.header?.tip?.color,
                      }"
                      class="t-2 h-[16px] max-w-[583px] truncate text-[12px] leading-[16px]"
                    >
                      {{ item?.header?.tip?.title }}
                    </div>
                  </div>
                  <div class="text-[12px] whitespace-nowrap text-[#292A2E]/60">
                    {{ item?.header?.serialNumber }}
                  </div>
                </div></template
              ></ElPopover
            >
            <ElPopover
              v-else-if="item?.header?.tip?.title && item?.header?.tips?.length > 1"
              :teleported="false"
              popper-class="tips-popover !p-[12px_16px] max-w-[740px]"
              placement="bottom"
              :show-after="200"
              :popper-options="popperOptions"
              ><div
                v-for="(el, i) in item?.header?.tips"
                :key="el.title"
                :style="{ color: el?.color }"
                :class="i === item?.header?.tips?.length - 1 ? '' : 'mb-[8px]'"
                class="flex gap-x-[4px] text-[14px]"
              >
                <div class="mt-[2px] flex !size-[16px] shrink-0 overflow-hidden">
                  <CommonImage :src="item?.header?.tip?.ico" class="size-full object-cover" alt="ico" />
                </div>
                <span class="min-h-[18px] text-[14px] leading-[18px]">{{ el.title }}</span>
              </div>
              <template #reference
                ><div
                  v-if="item?.header?.tip?.title"
                  :style="{
                    width: '740px',
                    background: item?.header?.tip?.bgColor,
                  }"
                  class="absolute top-[18px] right-[18px] left-[18px] z-[1] flex cursor-pointer items-center justify-between gap-x-[20px] p-[6px_8px]"
                >
                  <div class="flex items-center gap-x-[4px]">
                    <div class="mt-[2px] flex !size-[16px] shrink-0 overflow-hidden">
                      <CommonImage :src="item?.header?.tip?.ico" class="size-full object-cover" alt="ico" />
                    </div>
                    <div
                      :style="{
                        color: item?.header?.tip?.color,
                      }"
                      class="item-center flex gap-x-[16px] text-[12px]"
                    >
                      <div class="t-3 h-[16px] max-w-[583px] truncate text-[12px] leading-[16px]">
                        {{ item?.header?.tip?.title }}
                      </div>
                      <div
                        class="flex items-center gap-x-[2px]"
                        :style="{
                          color: item?.header?.tip.color,
                        }"
                      >
                        <span class="h-[16px] text-[13px] leading-[16px] text-[#CF9F02]">{{
                          item?.header?.tips?.length
                        }}</span>
                        <CommonImage
                          alt="right-icon"
                          class="!size-[14px]"
                          src="https://resources1.interface003.com/web30/2026/07/15/944825fc-0041-470b-900e-e09eff0eaaed.png"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="text-[13px] whitespace-nowrap text-[#7d818A]">
                    {{ item?.header?.serialNumber }}
                  </div>
                </div></template
              ></ElPopover
            >
            <div
              v-if="item?.bgImage && item?.bgImage[1]"
              class="absolute bottom-[40px] left-1/2 z-[1] flex h-[360px] w-[500px] shrink-0 -translate-x-1/2 overflow-hidden"
            >
              <CommonImage class="size-full object-cover" :src="item?.bgImage && item?.bgImage[1]" />
            </div>
            <div id="licenseContent" class="licenseContent relative z-[2]">
              <component
                :is="regulatorDetailHref ? regulatorDetailLink : 'div'"
                v-bind="regulatorDetailHref ? { to: regulatorDetailHref, target: '_blank' } : {}"
                class="relative flex flex-col items-center justify-center"
                :class="regulatorDetailHref ? 'cursor-pointer transition-opacity hover:opacity-80' : ''"
              >
                <div
                  v-if="!item?.header?.tip?.title && !item?.header?.tips?.length"
                  class="absolute top-[0px] -right-[12px] z-[1] text-right text-[12px] text-[#7D818A] rtl:right-auto rtl:-left-[12px] rtl:text-left"
                >
                  {{ item?.header?.serialNumber }}
                </div>
                <div
                  class="flex !h-[90px] !w-auto max-w-full min-w-[160px] shrink-0 overflow-hidden"
                  :class="item?.header?.tip?.title ? '' : 'mt-[7px]'"
                >
                  <CommonImage
                    class="size-full object-cover"
                    :src="item?.header?.logo"
                    alt="logo"
                    @error="useImageFallback"
                    loading="lazy"
                  />
                </div>
                <div
                  class="words-break-lines border-b-[0.5px] border-solid border-[#E5E6EB] px-[12px] pb-[8px] text-center text-[22px] font-[500] text-[#292A2E]"
                >
                  {{ item?.header?.name }}
                </div>

                <div class="mt-[8px] flex items-center justify-center gap-x-[6px]">
                  <div class="text-[14px] text-[#292A2E]">
                    {{ item?.header?.licenseName }}
                  </div>
                  <ElPopover
                    v-if="item?.header?.levelIcon"
                    :teleported="false"
                    popper-class="license-popover-s"
                    placement="bottom"
                    :show-after="200"
                    :popper-options="popperOptions"
                    ><p class="text-[14px] text-[#292A2E]">
                      {{ item?.header?.levelDesc }}
                    </p>
                    <template #reference
                      ><div class="flex h-[20px] items-center">
                        <div class="flex !h-[14px] !w-auto max-w-full shrink-0 cursor-pointer overflow-hidden">
                          <CommonImage :src="item?.header?.levelIcon" class="size-full object-cover" alt="levelIcon" />
                        </div></div></template
                  ></ElPopover>
                </div>
              </component>
              <ul
                ref="mainUl"
                class="main-ul mt-[20px] flex flex-wrap"
                :class="[
                  isTopBottom ? 'll-ul' : 'main-ul-chinese',
                  {
                    'main-ul-has-other': hasOtherItems,
                  },
                ]"
              >
                <li :class="!isTopBottom ? '!flex !items-center' : ''">
                  <div class="label-name">{{ item.isMsb ? t('84708') : t('82382') }}{{ isTopBottom ? '' : '：' }}</div>
                  <div
                    class="flex h-[18px] !w-fit items-center justify-center rounded-[2px] px-[4px] text-[12px] leading-[18px]"
                    :style="{
                      background: item?.regulationStatusBgColor,
                      color: item?.regulationStatusColor,
                    }"
                  >
                    {{ item?.regulationStatus }}
                  </div>
                </li>
                <li :class="!isTopBottom ? '!flex !items-center' : ''">
                  <div class="label-name">{{ item.isMsb ? t('84704') : t('84586') }}{{ isTopBottom ? '' : '：' }}</div>
                  <div class="flex w-full items-center truncate" :class="!isTopBottom ? '!flex !items-center' : ''">
                    <div class="inline-flex min-w-0 flex-1 truncate text-[14px] font-[600] text-[#292A2E]">
                      {{ item?.regulationNumber }}
                    </div>
                    <div
                      class="mx-[2px] ml-[8px] flex !h-[18px] !w-auto shrink-0 overflow-hidden rtl:mr-[8px] rtl:ml-[0px]"
                    >
                      <CommonImage
                        v-if="item?.newLicenseIcon"
                        class="size-full object-cover"
                        :src="item?.newLicenseIcon"
                        alt="new"
                      />
                    </div>
                  </div>
                </li>
                <li ref="regulationCountryLi">
                  <div class="label-name">{{ item.isMsb ? t('84703') : t('84560') }}{{ isTopBottom ? '' : '：' }}</div>
                  <div class="flex items-center gap-x-[8px]">
                    <div class="flex aspect-[11/7] !h-[14px] !w-[22px] min-w-[22px] shrink-0 overflow-hidden">
                      <CommonImage
                        class="size-full object-cover"
                        :src="item?.regulationCountryIcon"
                        :alt="item?.regulationCountryCode"
                      />
                    </div>
                    <div class="!w-fit text-[14px] font-[600] whitespace-nowrap text-[#292A2E]">
                      {{ item?.regulationCountryName }}
                    </div>
                    <ElPopover
                      v-if="
                        item?.replaceItems &&
                        item?.replaceItems[0] &&
                        item?.replaceItems[0].type === 2 &&
                        item?.regulationCountryCount > 0
                      "
                      :teleported="false"
                      popper-class="license-country-popover"
                      placement="bottom"
                      :show-after="200"
                      :popper-options="popperOptions"
                      ><div class="flex flex-wrap">
                        <div
                          v-for="(c, i) in item?.replaceItems && item?.replaceItems[0] && item?.replaceItems[0]?.items"
                          :key="i"
                          class="mb-[8px] flex w-[fit-content] cursor-pointer items-center gap-x-[6px] bg-[#F7F8FA] p-[6px_8px] hover:bg-[#EEF0F3]"
                          :class="
                            i === item?.replaceItems[0]?.items?.length - 1
                              ? 'mr-[0px] rtl:ml-[0px]'
                              : 'mr-[8px] rtl:mr-[0px] rtl:ml-[8px]'
                          "
                          @mouseenter="
                            handleHoverChildLicense({
                              ...c.tap,
                              childProjectCode: item?.relatedTrader?.traderCode || '',
                            })
                          "
                          @click="openChildLicense(c.tap, item?.replaceItems[0]?.items, i)"
                        >
                          <div class="flex !h-[14px] !w-auto shrink-0 overflow-hidden">
                            <CommonImage class="size-full object-cover" :src="c.countryFlag" :alt="c.countryName" />
                          </div>
                          <span class="text-[14px] text-[#292A2E]">{{ c.countryName }}</span>
                        </div>
                      </div>
                      <template #reference
                        ><div
                          ref="regulationCountryCount"
                          class="inline-flex h-[18px] !w-[fit-content] cursor-pointer items-center justify-center rounded-[2px] border-[0.5px] border-solid border-[#C9CDD4] leading-[18px]"
                        >
                          <span class="px-[3px] text-[12px] whitespace-nowrap text-[#7A7A7A]">{{
                            item?.regulationCountryCount
                          }}</span>
                        </div></template
                      ></ElPopover
                    >
                  </div>
                </li>
                <li>
                  <div class="label-name">{{ t('84561') }}{{ isTopBottom ? '' : '：' }}</div>
                  <div class="flex items-center gap-x-[8px]">
                    <div class="text-[14px] font-[600] text-[#292A2E]">
                      {{ item.showEffectiveTime }}
                    </div>
                    <ElPopover
                      v-if="item?.isReviewing"
                      :teleported="false"
                      :disabled="!item?.reviewingBubble"
                      popper-class="license-popover-fhz"
                      placement="top"
                      :show-after="200"
                      :popper-options="popperOptions"
                      ><span class="text-[14px] text-[#292A2E]">{{ item?.reviewingBubble }}</span>
                      <template #reference
                        ><span class="cursor-pointer text-[14px] font-[600] whitespace-nowrap text-[#164B71]">{{
                          item?.reviewingText
                        }}</span></template
                      ></ElPopover
                    >
                  </div>
                </li>
                <li v-if="item?.holdingCompanyName">
                  <div class="label-name">{{ item.isMsb ? t('84706') : t('84562') }}{{ isTopBottom ? '' : '：' }}</div>
                  <div class="inline text-[14px] font-[600] text-[#292A2E]">
                    <div class="inline">
                      <!-- 有公司了 在做跳转功能 -->
                      <!-- <div
                        v-if="item?.holdingCompanyId"
                        class="words-break-lines inline text-[14px] leading-[20px] font-[600] text-[#164B71] hover:opacity-80"
                        target="_blank"
                      >
                        {{ item?.holdingCompanyName }}
                      </div> -->
                      <!-- <span class="words-break-lines leading-[21px]">{{ item?.holdingCompanyName }}</span> -->
                      <ExchangeDetailLicenseLineClampOne
                        :text-content="item?.holdingCompanyName"
                        :is-top-bottom="isTopBottom"
                        :is-last-main-item="lastMainItemKey === 'holdingCompanyName'"
                        :popper-options="popperOptions"
                      />
                    </div>
                  </div>
                </li>
                <li v-if="item?.kybContent" ref="kybLi">
                  <div class="label-name">{{ t('84563') }}{{ isTopBottom ? '' : '：' }}</div>
                  <div class="flex items-center gap-x-[6px] text-[14px] font-[600] text-[#292A2E]">
                    <ElPopover
                      v-if="item?.kybIcon"
                      :teleported="false"
                      popper-class="license-popover-kyb"
                      placement="top"
                      :popper-options="popperOptions"
                      :show-after="200"
                      ><p class="text-[14px] text-[#292A2E]">
                        {{ item?.kybDesc }}
                      </p>
                      <template #reference
                        ><div ref="kybImg" class="inline !w-fit cursor-pointer">
                          <div class="flex !h-[18px] !w-[auto] shrink-0 overflow-hidden object-cover align-sub">
                            <CommonImage :src="item?.kybIcon" class="size-full object-cover" alt="kyb" />
                          </div></div></template
                    ></ElPopover>
                    <div class="inline" :class="item?.kybIcon ? 'mr-[6px] rtl:mr-[0px] rtl:ml-[6px]' : ''">
                      <span class="words-break-lines leading-[21px]">{{ item?.kybContent }}</span>
                    </div>
                  </div>
                </li>
                <li v-if="item?.relatedTrader?.traderName">
                  <div class="label-name">{{ t('84564') }}{{ isTopBottom ? '' : '：' }}</div>
                  <div class="flex items-center gap-x-[6px] truncate">
                    <div
                      v-if="item?.relatedTrader?.iconUrl"
                      class="!mx-[0px] flex !size-[16px] !max-w-[16px] !min-w-[16px] shrink-0 overflow-hidden rounded-[2px]"
                    >
                      <CommonImage class="size-full object-cover" :src="item?.relatedTrader?.iconUrl" />
                    </div>

                    <ElPopover
                      v-if="showrelatedTrader"
                      :teleported="false"
                      popper-class="license-popover"
                      placement="top"
                      :popper-options="popperOptions"
                      :show-after="200"
                      ><span class="text-[14px] font-[500] text-[#292A2E]">{{ item?.relatedTrader?.traderName }}</span
                      ><template #reference
                        ><div ref="relatedTrader" class="cursor-pointer truncate text-[14px] font-[500] text-[#292A2E]">
                          {{ item?.relatedTrader?.traderName }}
                        </div></template
                      ></ElPopover
                    >
                    <div v-else ref="relatedTrader" class="min-w-[0px] flex-1 truncate">
                      <span class="truncate text-[14px] leading-[normal] font-[600] text-[#292A2E]">{{
                        item?.relatedTrader?.traderName
                      }}</span>
                    </div>
                  </div>
                </li>
                <li>
                  <div class="label-name">{{ t('84565') }}{{ isTopBottom ? '' : '：' }}</div>
                  <ElPopover
                    v-if="item?.shareTraderCodes?.length > 0"
                    :teleported="false"
                    popper-class="license-popover"
                    placement="top"
                    :show-after="200"
                    :popper-options="popperOptions"
                  >
                    <div
                      v-if="item.shareType === 3 || item.shareType === 1"
                      class="relative flex !w-max flex-col gap-y-[12px]"
                    >
                      <div
                        class="flex !w-full flex-col items-center gap-y-[4px] rounded-[4px] bg-[#f8f8f8] p-[8px_12px] text-center"
                      >
                        <div>
                          {{ t('84609') }}
                        </div>
                        <NuxtLink
                          :to="ExchangeDetailUrl(grantor?.alias)"
                          class="flex items-center justify-center gap-x-[6px]"
                          v-for="grantor in item.grantorTraderCodes"
                          :key="grantor?.alias"
                          target="_blank"
                        >
                          <div class="flex !size-[16px] shrink-0 overflow-hidden rounded-[2px]">
                            <CommonImage class="size-full object-cover" :src="grantor?.iconUrl" />
                          </div>
                          <span class="text-[14px] text-[#0D3F63]">{{ grantor?.traderName }}</span>
                        </NuxtLink>
                      </div>

                      <div
                        class="absolute top-[50%] left-[50%] z-[1] flex size-[24px] shrink-0 -translate-x-1/2 -translate-y-1/2 transform overflow-hidden rounded-[50%]"
                      >
                        <CommonImage class="size-full object-cover" :src="SHARE_ARROW_ICON" alt="SHARE_ARROW_ICON" />
                      </div>
                      <div
                        class="flex !w-full flex-col items-center gap-y-[4px] rounded-[4px] bg-[#f8f8f8] p-[8px_12px] text-center"
                      >
                        <div>
                          {{ t('84610') }}
                        </div>
                        <NuxtLink
                          :to="ExchangeDetailUrl(grantee?.alias)"
                          class="flex items-center justify-center gap-x-[6px]"
                          v-for="grantee in item.granteeTraderCodes"
                          :key="grantee?.alias"
                          target="_blank"
                        >
                          <div class="flex !size-[16px] shrink-0 overflow-hidden rounded-[2px]">
                            <CommonImage class="size-full object-cover" :src="grantee?.iconUrl" />
                          </div>
                          <span class="text-[14px] text-[#0D3F63]">{{ grantee?.traderName }}</span>
                        </NuxtLink>
                      </div>
                    </div>
                    <div v-else class="flex flex-col gap-y-[8px]">
                      <NuxtLink
                        v-for="trader in item?.shareTraderCodes.filter(
                          (item: any) => item?.traderName && item?.iconUrl && item?.alias,
                        )"
                        :key="trader?.alias"
                        :to="ExchangeDetailUrl(trader.alias)"
                        target="_blank"
                        class="group flex cursor-pointer items-center gap-x-[6px]"
                      >
                        <div class="flex !h-[16px] !w-[16px] min-w-[16px] shrink-0 overflow-hidden rounded-[2px]">
                          <CommonImage class="size-full object-cover" :src="trader?.iconUrl" alt="icon" />
                        </div>
                        <span class="text-[14px] text-[#164B71] group-hover:opacity-80">{{
                          trader?.traderName || '--'
                        }}</span></NuxtLink
                      >
                    </div>
                    <template #reference
                      ><span class="cursor-pointer text-[14px] font-[600] text-[#164B71]">
                        {{ item?.share }}
                      </span></template
                    ></ElPopover
                  >
                  <div v-else class="text-[14px] font-[600] text-[#292A2E]">
                    {{ item?.share }}
                  </div>
                </li>
                <!-- 生效地区 -->
                <li v-if="(item?.effectiveArea?.length > 0 && !item?.viewMore) || viewMoreFn('effectiveArea')">
                  <div class="label-name">{{ t('84701') }}{{ isTopBottom ? '' : '：' }}</div>
                  <ElPopover
                    :teleported="false"
                    popper-class="!w-[436px] !h-[330px] !p-[12px] !max-w-[436px]"
                    placement="top"
                    :show-after="200"
                    :popper-options="popperOptions"
                  >
                    <ExchangeDetailLicenseMapOfUSA :highlighted-states="item?.effectiveAreaData" />
                    <template #reference>
                      <span class="cursor-pointer font-[OutFit] text-[14px] font-[500] text-[#0D3F63]">{{
                        item?.effectiveAreaData?.length
                      }}</span>
                    </template></ElPopover
                  >
                </li>

                <li v-if="(item?.websites?.length > 0 && !item?.viewMore) || viewMoreFn('websites')">
                  <div class="label-name">{{ t('84937') }}{{ isTopBottom ? '' : '：' }}</div>
                  <ExchangeDetailLicenseLineClampOne
                    :text-content="joinField(item?.websites)"
                    :is-top-bottom="isTopBottom"
                    :is-last-main-item="lastMainItemKey === 'websites'"
                    :popper-options="popperOptions"
                  />
                </li>

                <li v-if="(item?.emails?.length && !item?.viewMore) || viewMoreFn('emails')">
                  <div class="label-name">{{ t('84939') }}{{ isTopBottom ? '' : '：' }}</div>
                  <ExchangeDetailLicenseLineClampOne
                    :is-top-bottom="isTopBottom"
                    :is-last-main-item="lastMainItemKey === 'emails'"
                    :text-content="joinField(item?.emails)"
                    :popper-options="popperOptions"
                  />
                </li>

                <li v-if="(item?.phones?.length && !item?.viewMore) || viewMoreFn('phones')">
                  <div class="label-name">{{ t('84940') }}{{ isTopBottom ? '' : '：' }}</div>
                  <ExchangeDetailLicenseLineClampOne
                    :is-top-bottom="isTopBottom"
                    :is-last-main-item="lastMainItemKey === 'phones'"
                    :text-content="joinField(item?.phones)"
                    :popper-options="popperOptions"
                  />
                </li>

                <li v-if="(item?.addresses?.length && !item?.viewMore) || viewMoreFn('addresses')">
                  <div class="label-name">{{ t('84938') }}{{ isTopBottom ? '' : '：' }}</div>
                  <ExchangeDetailLicenseLineClampOne
                    :is-top-bottom="isTopBottom"
                    :is-last-main-item="lastMainItemKey === 'addresses'"
                    :text-content="joinField(item?.addresses)"
                    :popper-options="popperOptions"
                  />
                </li>

                <li v-if="item?.industryAbbreviations">
                  <div class="label-name">{{ t('84597') }}{{ isTopBottom ? '' : '：' }}</div>
                  <ExchangeDetailLicenseLineClampOne
                    :is-top-bottom="isTopBottom"
                    :is-last-main-item="lastMainItemKey === 'industryAbbreviations'"
                    :text-content="item.industryAbbreviations"
                    :popper-options="popperOptions"
                  />
                </li>

                <li v-if="item?.tradingMethod">
                  <div class="label-name">{{ t('82681') }}{{ isTopBottom ? '' : '：' }}</div>
                  <ExchangeDetailLicenseLineClampOne
                    :is-top-bottom="isTopBottom"
                    :is-last-main-item="lastMainItemKey === 'tradingMethod'"
                    :text-content="item.tradingMethod"
                    :popper-options="popperOptions"
                  />
                </li>

                <li v-if="item?.customerType?.length && !getContactFieldsCount()">
                  <div class="label-name customer-1">{{ t('84568') }}{{ isTopBottom ? '' : '：' }}</div>
                  <ExchangeDetailLicenseLineClampOne
                    :is-top-bottom="isTopBottom"
                    :is-last-main-item="lastMainItemKey === 'customerType'"
                    :text-content="joinField(item?.customerType)"
                    :popper-options="popperOptions"
                  />
                </li>
              </ul>
              <!-- 其他模块 -->
              <div class="flex justify-between">
                <div id="otherDiv" ref="otherDiv" :style="otherLayoutStyle" class="gap-[10px]">
                  <ul
                    id="otherUl"
                    ref="otherUl"
                    class="other-ul"
                    :class="{
                      'll-ul': isTopBottom,
                      'other-ul-has-items': hasOtherItems,
                      'other-ul-empty': !hasOtherItems,
                    }"
                  >
                    <li v-if="item?.businessPermissions?.length">
                      <div class="label-name">{{ t('84569') }}{{ isTopBottom ? '' : '：' }}</div>
                      <div
                        v-if="!isExceeded"
                        ref="textContainer"
                        :class="[!isTopBottom ? '!w-[384px]' : '!w-[458px]', lineClampClass]"
                        class="words-break-lines h-auto text-[14px] font-[600] text-ellipsis text-[#292A2E]"
                      >
                        <span>{{ item?.businessPermissions[0] }}</span>
                      </div>
                      <ElPopover
                        v-else
                        :teleported="false"
                        popper-class="license-popover"
                        placement="top"
                        :show-after="200"
                        :popper-options="popperOptions"
                        ><p class="text-[14px] text-[#292A2E]">
                          {{ item?.businessPermissions[0] }}
                        </p>
                        <template #reference
                          ><div
                            :class="[!isTopBottom ? '!w-[384px]' : '!w-[458px]', lineClampClass]"
                            class="cursor-pointer text-[14px] font-[600] text-ellipsis text-[#292A2E]"
                          >
                            <span>{{ item?.businessPermissions[0] }}</span>
                          </div></template
                        ></ElPopover
                      >
                    </li>
                    <li v-if="item?.customerType?.length && getContactFieldsCount()">
                      <div class="label-name customer-2">{{ t('84568') }}{{ isTopBottom ? '' : '：' }}</div>
                      <div
                        v-if="!isTypeExceeded"
                        ref="customerTypeText"
                        class="words-break-lines text-[14px] font-[600] text-ellipsis text-[#292A2E]"
                        :class="[!isTopBottom ? '!w-[384px]' : '!w-[458px]', 'words-break-lines line-clamp-2 h-auto']"
                      >
                        {{ joinField(item?.customerType) }}
                      </div>
                      <ElPopover
                        v-else
                        :teleported="false"
                        popper-class="license-popover"
                        placement="top"
                        :show-after="200"
                        :popper-options="popperOptions"
                        ><div class="text-[14px] text-[#292A2E]">
                          {{ joinField(item?.customerType) }}
                        </div>
                        <template #reference
                          ><div
                            :class="!isTopBottom ? '!w-[384px]' : '!w-fit !max-w-[458px]'"
                            class="cursor-pointer truncate text-[14px] font-[600] text-[#292A2E]"
                          >
                            {{ joinField(item?.customerType) }}
                          </div></template
                        ></ElPopover
                      >
                    </li>
                  </ul>
                  <ElPopover
                    v-if="item?.viewMore"
                    :teleported="false"
                    popper-class="license-popover"
                    placement="top"
                    :show-after="200"
                    :popper-options="popperOptions"
                    ><ul class="flex flex-col">
                      <li v-if="item?.effectiveArea?.length && !viewMoreFn('effectiveArea')" class="mb-[12px]">
                        <div class="label-name mb-[4px] text-[#292A2E]/60">
                          {{ t('84937') }}{{ isTopBottom ? '' : '：' }}
                        </div>
                        <div class="text-[#292A2E]">
                          {{ item?.effectiveArea.length }}
                        </div>
                      </li>

                      <li v-if="item?.websites?.length && !viewMoreFn('websites')" class="mb-[12px]">
                        <div class="label-name mb-[4px] text-[#292A2E]/60">
                          {{ t('84937') }}{{ isTopBottom ? '' : '：' }}
                        </div>
                        <div class="text-[#292A2E]">
                          {{ joinField(item?.websites) }}
                        </div>
                      </li>
                      <li v-if="item?.emails?.length && !viewMoreFn('emails')" class="mb-[12px]">
                        <div class="label-name mb-[4px] text-[#292A2E]/60">
                          {{ t('84939') }}{{ isTopBottom ? '' : '：' }}
                        </div>
                        <div class="text-[#292A2E]">
                          {{ joinField(item?.emails) }}
                        </div>
                      </li>
                      <li v-if="item?.phones?.length && !viewMoreFn('phones')" class="mb-[12px]">
                        <div class="label-name mb-[4px] text-[#292A2E]/60">
                          {{ t('84940') }}{{ isTopBottom ? '' : '：' }}
                        </div>
                        <div class="text-[#292A2E]">
                          {{ joinField(item?.phones) }}
                        </div>
                      </li>
                      <li v-if="item?.addresses?.length && !viewMoreFn('addresses')">
                        <div class="label-name mb-[4px] text-[#292A2E]/60">
                          {{ t('84938') }}{{ isTopBottom ? '' : '：' }}
                        </div>
                        <div class="text-[#292A2E]">
                          {{ joinField(item?.addresses) }}
                        </div>
                      </li>
                    </ul>
                    <template #reference
                      ><div class="has-more flex w-fit cursor-pointer items-center text-[14px] text-[#164b71]">
                        <span>{{ t('84570') }}</span>
                        <CommonImage
                          alt="right-icon"
                          class="!size-[16px] rtl:rotate-180"
                          src="https://resources1.interface003.com/web30/2026/07/15/998c539d-70cf-44e4-aa09-142898840020.png"
                        /></div></template
                  ></ElPopover>
                </div>
              </div>
              <!-- 客户类型模块 不需要隐藏, 必须存在h-17px -->
              <div class="flex h-[17px] w-full items-center justify-center">
                <ElPopover
                  v-if="item?.replaceItems && item?.replaceItems[0] && item?.replaceItems[0].type === 2"
                  :teleported="false"
                  popper-class="license-country-popover"
                  placement="top"
                  :show-after="200"
                  :popper-options="popperOptions"
                  ><div class="flex flex-wrap">
                    <div
                      v-for="(c, i) in item?.replaceItems && item?.replaceItems[0] && item?.replaceItems[0]?.items"
                      :key="i"
                      class="mb-[8px] flex w-[fit-content] cursor-pointer items-center gap-x-[6px] bg-[#F7F8FA] p-[6px_8px] hover:bg-[#EEF0F3]"
                      :class="
                        i === item?.replaceItems[0]?.items?.length - 1
                          ? 'mr-[0px] rtl:ml-[0px]'
                          : 'mr-[8px] rtl:mr-[0px] rtl:ml-[8px]'
                      "
                      @mouseenter="
                        handleHoverChildLicense({
                          ...c.tap,
                          childProjectCode: item?.relatedTrader?.traderCode || '',
                        })
                      "
                      @click="openChildLicense(c.tap, item?.replaceItems[0]?.items, i)"
                    >
                      <div class="flex !h-[14px] !w-auto shrink-0 overflow-hidden">
                        <CommonImage class="size-full object-cover" :src="c.countryFlag" :alt="c.countryName" />
                      </div>
                      <span class="text-[14px] text-[#292A2E]">{{ c.countryName }}</span>
                    </div>
                  </div>
                  <template #reference
                    ><span
                      class="b1 inline-flex cursor-pointer items-center text-[12px] text-[#292A2E]/60"
                      v-html="bottomHtml"
                    ></span></template></ElPopover
                ><span
                  v-else-if="item?.bottomLabel"
                  class="b2 inline-flex cursor-pointer items-center text-[12px] text-[#292A2E]/60"
                  @click="openFn"
                  @mouseenter="localStoreLicenseDataFunc"
                  v-html="bottomHtml"
                ></span>
                <span
                  v-else-if="item?.sections?.length"
                  class="b2 inline-flex cursor-pointer items-center text-[12px] text-[#292A2E]/60"
                  @click="openMsbFn"
                  v-html="bottomHtml"
                ></span>
              </div>
            </div>
          </div>
          <div class="mt-[12px] flex flex-col items-center justify-center gap-y-[6px]">
            <div class="group flex gap-x-[10px]">
              <NuxtLink
                v-if="item?.attachments?.length > 0"
                ref="attachmentsBtn"
                :to="buildCertificateHref(item?.regulationId)"
                target="_blank"
                class="relative flex h-[44px] w-[fit-content] min-w-[220px] flex-shrink-0 cursor-pointer items-center justify-center gap-x-[4px] overflow-hidden rounded-[100px] border-0 bg-[#F8F8F8] p-[10px] text-[14px] leading-[22px] font-[600] whitespace-nowrap text-[#292A2E] after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:bg-transparent after:transition-colors hover:after:bg-[rgba(0,0,0,0.03)]"
                @click="trackBottomClick('证明文件')"
              >
                <CommonImage
                  class="!size-[20px]"
                  src="https://resources1.interface003.com/web30/2026/07/15/a4602baf-7427-4af0-9b5d-f07be1e41901.png"
                />
                <span>{{ t('84514') }}</span
                ><span v-if="item?.attachments?.length > 1">（{{ item?.attachments?.length }}）</span></NuxtLink
              >
              <button
                v-if="hasChangeLog"
                ref="changeLogCountBtn"
                type="button"
                class="relative flex h-[44px] w-[fit-content] min-w-[220px] flex-shrink-0 cursor-pointer items-center justify-center gap-x-[4px] overflow-hidden rounded-[100px] border-0 bg-[#F8F8F8] p-[10px] text-[14px] leading-[22px] font-[600] whitespace-nowrap text-[#292A2E] after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:bg-transparent after:transition-colors hover:after:bg-[rgba(0,0,0,0.03)]"
                @click="getChangLog(item?.regulationId)"
              >
                <CommonImage
                  class="!size-[20px]"
                  src="https://resources1.interface003.com/web30/2026/07/15/486eebda-12f2-402f-b38b-461b780d393f.png"
                /><span>{{ t('84571') }}</span
                ><span v-if="item?.changeLogCount > 1">{{ changeLogCount }}</span>
              </button>

              <!-- 关联风险Icon: https://resources1.interface003.com/web30/2026/07/15/6f2c0b7c-1541-4e67-88f0-75da001847a5.png -->
            </div>
            <div class="text-[12px] text-[#7D818A]">
              <span style="direction: ltr">{{ item?.updateTime }}</span>
            </div>
          </div>
          <ExchangeDetailLicenseHistoryDialog
            :show="showRegHis"
            :regulation-id="item?.regulationId"
            :project-code="projectCode"
            :result="changLogList"
            @closeReghisFn="closeReghisFn"
          />
        </div>
        <ExchangeDetailLicenseRiskStatement ref="riskstatement" :project-code="projectCode" />
      </div>
    </ElDialog>
  </ClientOnly>
</template>

<style scoped lang="less">
.license-switch-animation {
  animation: license-switch-in 180ms ease-out;
}

.license-switch-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: license-switch-spin 0.8s linear infinite;
}

@keyframes license-switch-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes license-switch-spin {
  0% {
    transform: rotate(0);
  }

  100% {
    transform: rotate(360deg);
  }
}

:global(.dialog-regulatory .el-dialog__body #popreginfo) {
  background-size: 100% 100%;
  background-position: center;
  background-repeat: no-repeat;
}
:global(.dialog-regulatory img) {
  max-width: 100%;
}

/* 三组列表共享样式 */
:global(.dialog-regulatory :is(.main-ul, .contact-ul) li) {
  margin-bottom: 10px;
  display: flex;
  width: calc(50% - 10px);
  align-items: flex-start;
  column-gap: 4px;
  overflow: hidden;
  p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: calc(50% - 10px);
  }
}

:global(.dialog-regulatory :is(.other-ul) li) {
  margin-bottom: 10px;
  display: flex;
  min-width: calc(50% - 10px);
  align-items: flex-start;
  column-gap: 4px;
}

:global(.dialog-regulatory :is(.main-ul, .contact-ul, .other-ul) li div) {
  width: fit-content;
}
:global(.dialog-regulatory :is(.main-ul, .contact-ul, .other-ul) li .label-name) {
  width: fit-content;
  white-space: nowrap;
  font-size: 14px;
  color: #7d818a;
}
/* LTR mr-20 / RTL ml-20 */
:global(.dialog-regulatory :is(.main-ul, .contact-ul, .other-ul) li:nth-child(odd)) {
  margin-inline-end: 20px;
}

/* 中文版限宽 */
:global(.dialog-regulatory :is(.main-ul-chinese, .contact-ul-chinese) li div:last-child) {
  width: fit-content;
  max-width: 264px;
}

/* other-ul 整行 */
:global(.dialog-regulatory .other-ul li) {
  width: 100%;
}
:global(.dialog-regulatory .other-ul li:last-child) {
  margin-bottom: 0;
}
/* 单项基础高度 */
:global(.dialog-regulatory .main-ul-has-other.main-ul-chinese > li) {
  min-height: 20px;
}
:global(.dialog-regulatory .main-ul-has-other.ll-ul > li) {
  min-height: 44px;
}

:global(.dialog-regulatory #otherDiv) {
  min-height: var(--other-ul-min-height);
}

:global(.dialog-regulatory .has-more) {
  min-height: var(--has-more-height);
}

:global(.dialog-regulatory .ll-ul li) {
  flex-direction: column;
  column-gap: 0;
  row-gap: 4px;
}

/* 末项为奇数时占满整行 */
:global(.dialog-regulatory :is(.main-ul, .contact-ul) li:last-child:nth-child(odd)) {
  margin-inline-end: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: fit-content !important;
}
:global(.dialog-regulatory :is(.main-ul, .contact-ul) li:last-child:nth-child(odd) div:last-child) {
  min-width: 0;
  max-width: 100%;
  width: fit-content !important;
}
</style>

<style lang="less">
.dividing-lines::before {
  position: absolute;
  left: 0;
  top: 50%;
  display: inline-block;
  height: 12px;
  width: 0.5px;
  transform: translateY(-50%);
  background: rgb(22 75 113 / 0.4);
  content: '';
}
/* 牌照弹框内所有 ElPopover 统一样式：移除 Element Plus 默认边框，用阴影替代 */
.tips-popover,
.license-popover-kyb,
.license-popover-fhz,
.license-popover,
.license-country-popover,
.license-popover-s {
  width: max-content !important;
  max-width: 696px !important;
  color: #292a2e !important;
  font-size: 14px !important;
  background: #fff !important;
  border: none !important;
  border-radius: 4px !important;
  padding: 12px 16px !important;
  min-width: unset !important;
  align-items: flex-start !important;
  div,
  ul > li,
  p {
    overflow-wrap: break-word;
    word-break: normal;
    white-space: normal;
    line-height: normal !important;
    vertical-align: middle !important;
  }
  span {
    vertical-align: middle !important;
    line-height: normal !important;
  }
}
.license-country-popover {
  padding: 12px 16px 4px !important;
}
</style>
